import { useEffect, useState, useCallback } from "react";
import {
  Modal, Button, Input, Select, Switch, Tabs, Form, message,
  Collapse, Tag, Tooltip, Popconfirm, Space, InputNumber,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, DragOutlined, UpOutlined, DownOutlined,
  ExportOutlined, EyeOutlined,
} from "@ant-design/icons";
import api from "../../../../../core/axios/axiosInstance";
import FlowPreview from "./FlowPreview";

const { Panel } = Collapse;

/* ─── Constants ─────────────────────────────────────────────── */
const CATEGORIES = [
  { value: "LEAD_GENERATION", label: "Lead Generation" },
  { value: "SIGN_UP",         label: "Sign Up" },
  { value: "SIGN_IN",         label: "Sign In" },
  { value: "APPOINTMENT_BOOKING", label: "Appointment Booking" },
  { value: "CONTACT_US",      label: "Contact Us" },
  { value: "CUSTOMER_SUPPORT", label: "Customer Support" },
  { value: "SURVEY",          label: "Survey" },
  { value: "OTHER",           label: "Other" },
];

const COMPONENT_TYPES = [
  { type: "TextHeading",        label: "Heading",         group: "Display",   icon: "H" },
  { type: "TextSubheading",     label: "Subheading",      group: "Display",   icon: "H2" },
  { type: "TextBody",           label: "Body Text",       group: "Display",   icon: "T" },
  { type: "RichText",           label: "Rich Text",       group: "Display",   icon: "MD" },
  { type: "Image",              label: "Image",           group: "Display",   icon: "🖼" },
  { type: "TextInput",          label: "Text Input",      group: "Input",     icon: "[ ]" },
  { type: "TextArea",           label: "Text Area",       group: "Input",     icon: "[__]" },
  { type: "DatePicker",         label: "Date Picker",     group: "Input",     icon: "📅" },
  { type: "Dropdown",           label: "Dropdown",        group: "Input",     icon: "▼" },
  { type: "RadioButtonsGroup",  label: "Radio Buttons",   group: "Input",     icon: "○" },
  { type: "CheckboxGroup",      label: "Checkboxes",      group: "Input",     icon: "☑" },
  { type: "ChipsSelector",      label: "Chips Selector",  group: "Input",     icon: "🏷" },
  { type: "OptIn",              label: "Opt-In",          group: "Input",     icon: "✔" },
  { type: "PhotoPicker",        label: "Photo Upload",    group: "Input",     icon: "📷" },
  { type: "DocumentPicker",     label: "Document Upload", group: "Input",     icon: "📄" },
  { type: "EmbeddedLink",       label: "Embedded Link",   group: "Navigation", icon: "🔗" },
  { type: "Footer",             label: "Footer / Button", group: "Navigation", icon: "▬" },
];

const CONTACT_FIELDS = [
  { value: "firstname",   label: "First Name" },
  { value: "lastname",    label: "Last Name" },
  { value: "email",       label: "Email" },
  { value: "phone",       label: "Phone" },
  { value: "company",     label: "Company" },
  { value: "designation", label: "Job Title" },
  { value: "website",     label: "Website" },
  { value: "linkedin",    label: "LinkedIn" },
  { value: "instagram",   label: "Instagram" },
  { value: "notes",       label: "Notes (append)" },
];

const INPUT_TYPES = [
  { value: "text",     label: "Text" },
  { value: "email",    label: "Email" },
  { value: "phone",    label: "Phone" },
  { value: "number",   label: "Number" },
  { value: "password", label: "Password" },
  { value: "passcode", label: "Passcode / OTP" },
];

const FOOTER_ACTIONS = [
  { value: "navigate",      label: "Go to another screen" },
  { value: "complete",      label: "Complete (submit form)" },
  { value: "data_exchange", label: "Data Exchange (server-driven)" },
];

/* ─── Helpers ────────────────────────────────────────────────── */
const uid = () => `comp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
const screenUid = () => `SCREEN_${Date.now()}`;

function defaultProps(type) {
  switch (type) {
    case "TextHeading":
    case "TextSubheading":
    case "TextBody":
    case "RichText":
      return { text: "" };
    case "Image":
      return { src: "", "scale-type": "contain", "alt-text": "" };
    case "TextInput":
      return { name: "", label: "", "input-type": "text", required: false };
    case "TextArea":
      return { name: "", label: "", required: false };
    case "DatePicker":
      return { name: "", label: "", required: false };
    case "Dropdown":
    case "RadioButtonsGroup":
    case "CheckboxGroup":
    case "ChipsSelector":
      return { name: "", label: "", required: false, "data-source": [] };
    case "OptIn":
      return { name: "", label: "" };
    case "PhotoPicker":
      return { name: "", label: "", "photo-source": "camera_gallery", "min-uploaded-photos": 0, "max-uploaded-photos": 1 };
    case "DocumentPicker":
      return { name: "", label: "", "min-uploaded-documents": 1, "max-uploaded-documents": 1 };
    case "EmbeddedLink":
      return { text: "", "on-click-action": { name: "open_url", url: "" } };
    case "Footer":
      return { label: "Submit", "on-click-action": { name: "complete", payload: {} } };
    default:
      return {};
  }
}

/* ─── Component Props Editor ─────────────────────────────────── */
const CompPropsEditor = ({ comp, screens, onChange }) => {
  const p = comp.props || {};
  const set = (key, val) => onChange({ ...comp, props: { ...p, [key]: val } });
  const setAction = (patch) => onChange({ ...comp, props: { ...p, "on-click-action": { ...(p["on-click-action"] || {}), ...patch } } });

  const optionsStr = (arr) => (arr || []).map((o) => o.title || o).join("\n");
  const parseOptions = (str) => str.split("\n").filter(Boolean).map((o, i) => ({ id: String(i + 1), title: o.trim() }));

  switch (comp.type) {
    case "TextHeading":
    case "TextSubheading":
    case "TextBody":
    case "RichText":
      return (
        <>
          <label style={lbl}>Text</label>
          <Input.TextArea value={p.text || ""} onChange={(e) => set("text", e.target.value)} rows={3} />
          {comp.type === "TextBody" && (
            <>
              <label style={lbl}>Font weight</label>
              <Select value={p["font-weight"] || "normal"} onChange={(v) => set("font-weight", v)} size="small" style={{ width: "100%" }}
                options={[{ value: "normal" }, { value: "bold" }, { value: "italic" }, { value: "bold_italic" }]} />
            </>
          )}
        </>
      );

    case "TextInput":
      return (
        <>
          <label style={lbl}>Field name (unique ID)</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" placeholder="e.g. first_name" />
          <label style={lbl}>Label</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <label style={lbl}>Input type</label>
          <Select value={p["input-type"] || "text"} onChange={(v) => set("input-type", v)} size="small" style={{ width: "100%" }} options={INPUT_TYPES} />
          <label style={lbl}>Helper text (optional)</label>
          <Input value={p["helper-text"] || ""} onChange={(e) => set("helper-text", e.target.value)} size="small" />
          <label style={lbl}>Regex pattern (optional)</label>
          <Input value={p.pattern || ""} onChange={(e) => set("pattern", e.target.value)} size="small" placeholder="^[A-Za-z ]+$" />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Min chars</label>
              <InputNumber min={1} value={p["min-chars"] || null} onChange={(v) => set("min-chars", v)} size="small" style={{ width: "100%" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Max chars</label>
              <InputNumber min={1} value={p["max-chars"] || null} onChange={(v) => set("max-chars", v)} size="small" style={{ width: "100%" }} />
            </div>
          </div>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Switch size="small" checked={p.required || false} onChange={(v) => set("required", v)} />
            <span style={{ fontSize: 12 }}>Required</span>
          </div>
        </>
      );

    case "TextArea":
      return (
        <>
          <label style={lbl}>Field name</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" />
          <label style={lbl}>Label</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <label style={lbl}>Max length (default 600)</label>
          <InputNumber min={1} max={4000} value={p["max-length"] || null} onChange={(v) => set("max-length", v)} size="small" style={{ width: "100%" }} />
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Switch size="small" checked={p.required || false} onChange={(v) => set("required", v)} />
            <span style={{ fontSize: 12 }}>Required</span>
          </div>
        </>
      );

    case "DatePicker":
      return (
        <>
          <label style={lbl}>Field name</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" />
          <label style={lbl}>Label</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <label style={lbl}>Min date (YYYY-MM-DD, optional)</label>
          <Input value={p["min-date"] || ""} onChange={(e) => set("min-date", e.target.value)} size="small" placeholder="2026-01-01" />
          <label style={lbl}>Max date (YYYY-MM-DD, optional)</label>
          <Input value={p["max-date"] || ""} onChange={(e) => set("max-date", e.target.value)} size="small" placeholder="2026-12-31" />
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Switch size="small" checked={p.required || false} onChange={(v) => set("required", v)} />
            <span style={{ fontSize: 12 }}>Required</span>
          </div>
        </>
      );

    case "Dropdown":
    case "RadioButtonsGroup":
    case "CheckboxGroup":
    case "ChipsSelector":
      return (
        <>
          <label style={lbl}>Field name</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" />
          <label style={lbl}>Label</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <label style={lbl}>Options (one per line)</label>
          <Input.TextArea
            key={comp.id || comp.type}
            defaultValue={optionsStr(p["data-source"])}
            onBlur={(e) => set("data-source", parseOptions(e.target.value))}
            rows={4}
            placeholder={"Option 1\nOption 2\nOption 3"}
          />
          {(comp.type === "CheckboxGroup" || comp.type === "ChipsSelector") && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Min selected</label>
                <InputNumber min={0} value={p["min-selected-items"] || null} onChange={(v) => set("min-selected-items", v)} size="small" style={{ width: "100%" }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Max selected</label>
                <InputNumber min={1} value={p["max-selected-items"] || null} onChange={(v) => set("max-selected-items", v)} size="small" style={{ width: "100%" }} />
              </div>
            </div>
          )}
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Switch size="small" checked={p.required || false} onChange={(v) => set("required", v)} />
            <span style={{ fontSize: 12 }}>Required</span>
          </div>
        </>
      );

    case "OptIn":
      return (
        <>
          <label style={lbl}>Field name</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" />
          <label style={lbl}>Label text (max 120 chars)</label>
          <Input.TextArea value={p.label || ""} onChange={(e) => set("label", e.target.value)} rows={2} />
          <label style={lbl}>Link URL (optional — "Read more")</label>
          <Input value={p["on-click-action"]?.url || ""} onChange={(e) => setAction({ name: "open_url", url: e.target.value })} size="small" placeholder="https://example.com/terms" />
        </>
      );

    case "PhotoPicker":
      return (
        <>
          <label style={lbl}>Field name</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" />
          <label style={lbl}>Label</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <label style={lbl}>Photo source</label>
          <Select value={p["photo-source"] || "camera_gallery"} onChange={(v) => set("photo-source", v)} size="small" style={{ width: "100%" }}
            options={[{ value: "camera_gallery", label: "Camera & Gallery" }, { value: "camera", label: "Camera only" }, { value: "gallery", label: "Gallery only" }]} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Min photos</label>
              <InputNumber min={0} max={30} value={p["min-uploaded-photos"] ?? 0} onChange={(v) => set("min-uploaded-photos", v)} size="small" style={{ width: "100%" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Max photos</label>
              <InputNumber min={1} max={30} value={p["max-uploaded-photos"] ?? 1} onChange={(v) => set("max-uploaded-photos", v)} size="small" style={{ width: "100%" }} />
            </div>
          </div>
          <label style={lbl}>Max file size (KB)</label>
          <InputNumber min={1} value={p["max-file-size-kb"] || null} onChange={(v) => set("max-file-size-kb", v)} size="small" style={{ width: "100%" }} />
        </>
      );

    case "DocumentPicker":
      return (
        <>
          <label style={lbl}>Field name</label>
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} size="small" />
          <label style={lbl}>Label</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Min docs</label>
              <InputNumber min={0} max={10} value={p["min-uploaded-documents"] ?? 1} onChange={(v) => set("min-uploaded-documents", v)} size="small" style={{ width: "100%" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Max docs</label>
              <InputNumber min={1} max={10} value={p["max-uploaded-documents"] ?? 1} onChange={(v) => set("max-uploaded-documents", v)} size="small" style={{ width: "100%" }} />
            </div>
          </div>
          <label style={lbl}>Max file size (KB)</label>
          <InputNumber min={1} value={p["max-file-size-kb"] || null} onChange={(v) => set("max-file-size-kb", v)} size="small" style={{ width: "100%" }} />
        </>
      );

    case "EmbeddedLink":
      return (
        <>
          <label style={lbl}>Link text (max 25 chars)</label>
          <Input value={p.text || ""} onChange={(e) => set("text", e.target.value)} size="small" />
          <label style={lbl}>Action</label>
          <Select value={p["on-click-action"]?.name || "open_url"} onChange={(v) => setAction({ name: v })} size="small" style={{ width: "100%" }}
            options={[{ value: "open_url", label: "Open URL" }, { value: "navigate", label: "Navigate to screen" }, { value: "data_exchange", label: "Data Exchange" }]} />
          {p["on-click-action"]?.name === "open_url" && (
            <>
              <label style={lbl}>URL</label>
              <Input value={p["on-click-action"]?.url || ""} onChange={(e) => setAction({ url: e.target.value })} size="small" />
            </>
          )}
          {p["on-click-action"]?.name === "navigate" && (
            <>
              <label style={lbl}>Target screen</label>
              <Select value={p["on-click-action"]?.next?.name || ""} onChange={(v) => setAction({ next: { type: "screen", name: v } })} size="small" style={{ width: "100%" }}
                options={screens.map((s) => ({ value: s.id, label: s.title || s.id }))} />
            </>
          )}
        </>
      );

    case "Footer":
      return (
        <>
          <label style={lbl}>Button label (max 35 chars)</label>
          <Input value={p.label || ""} onChange={(e) => set("label", e.target.value)} size="small" />
          <label style={lbl}>Action</label>
          <Select value={p["on-click-action"]?.name || "complete"} onChange={(v) => setAction({ name: v })} size="small" style={{ width: "100%" }} options={FOOTER_ACTIONS} />
          {p["on-click-action"]?.name === "navigate" && (
            <>
              <label style={lbl}>Next screen</label>
              <Select value={p["on-click-action"]?.next?.name || ""} onChange={(v) => setAction({ next: { type: "screen", name: v }, payload: p["on-click-action"]?.payload || {} })}
                size="small" style={{ width: "100%" }} options={screens.map((s) => ({ value: s.id, label: s.title || s.id }))} />
            </>
          )}
          <label style={lbl}>Left caption (optional, max 15 chars)</label>
          <Input value={p["left-caption"] || ""} onChange={(e) => set("left-caption", e.target.value)} size="small" />
          <label style={lbl}>Right caption (optional, max 15 chars)</label>
          <Input value={p["right-caption"] || ""} onChange={(e) => set("right-caption", e.target.value)} size="small" />
        </>
      );

    default:
      return <div style={{ color: "#9ca3af", fontSize: 12 }}>Select a component to configure it.</div>;
  }
};

const lbl = { fontSize: 11, color: "#6b7280", display: "block", marginTop: 8, marginBottom: 3 };

/* ─── Screen Editor ──────────────────────────────────────────── */
const ScreenEditor = ({ screen, screens, onUpdate, onDelete }) => {
  const [selectedCompId, setSelectedCompId] = useState(null);

  const setScreen = (patch) => onUpdate({ ...screen, ...patch });
  const comps = screen.components || [];

  const addComponent = (type) => {
    const c = { id: uid(), type, props: defaultProps(type) };
    setScreen({ components: [...comps, c] });
    setSelectedCompId(c.id);
  };

  const updateComp = (comp) => {
    setScreen({ components: comps.map((c) => c.id === comp.id ? comp : c) });
  };

  const deleteComp = (cid) => {
    setScreen({ components: comps.filter((c) => c.id !== cid) });
    setSelectedCompId(null);
  };

  const moveComp = (idx, dir) => {
    const arr = [...comps];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    setScreen({ components: arr });
  };

  const selectedComp = comps.find((c) => c.id === selectedCompId);

  const compGroups = ["Display", "Input", "Navigation"];
  const GROUP_COLOR = { Display: "#3B82F6", Input: "#10B981", Navigation: "#F59E0B" };

  const selectedMeta = selectedComp ? COMPONENT_TYPES.find((t) => t.type === selectedComp.type) : null;
  const selectedGroupColor = GROUP_COLOR[selectedMeta?.group] || "#7c3aed";

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>

      {/* LEFT: Component list */}
      <div style={{ width: 230, display: "flex", flexDirection: "column", borderRight: "1px solid #e5e7eb", background: "#fafafa" }}>
        {/* Screen settings */}
        <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid #e5e7eb" }}>
          <Input
            size="small"
            value={screen.title || ""}
            onChange={(e) => setScreen({ title: e.target.value })}
            placeholder="Screen title"
            style={{ fontWeight: 600, marginBottom: 8 }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[
              { label: "Terminal", key: "terminal", value: screen.terminal || false, onChange: (v) => setScreen({ terminal: v }) },
              ...(screen.terminal ? [{ label: "Success", key: "success", value: screen.success !== false, onChange: (v) => setScreen({ success: v }) }] : []),
              { label: "Refresh on back", key: "refresh", value: screen.refreshOnBack || false, onChange: (v) => setScreen({ refreshOnBack: v }) },
            ].map((toggle) => (
              <div key={toggle.key} onClick={() => toggle.onChange(!toggle.value)} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: 6,
                background: toggle.value ? "#7c3aed14" : "#f3f4f6",
                border: `1px solid ${toggle.value ? "#7c3aed44" : "#e5e7eb"}`,
                cursor: "pointer", userSelect: "none",
              }}>
                <Switch size="small" checked={toggle.value} onChange={toggle.onChange} onClick={(_, e) => e.stopPropagation()} />
                <span style={{ fontSize: 10, fontWeight: 500, color: toggle.value ? "#7c3aed" : "#6b7280" }}>{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Component list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
            Components on screen
          </div>
          {comps.length === 0 && (
            <div style={{ fontSize: 11, color: "#b0b7c0", textAlign: "center", paddingTop: 24, lineHeight: 1.6 }}>
              Pick components from the palette →<br />
              <span style={{ fontSize: 10 }}>They appear here</span>
            </div>
          )}
          {comps.map((comp, idx) => {
            const meta = COMPONENT_TYPES.find((t) => t.type === comp.type);
            const isSel = selectedCompId === comp.id;
            const gc = GROUP_COLOR[meta?.group] || "#7c3aed";
            return (
              <div
                key={comp.id}
                onClick={() => setSelectedCompId(comp.id)}
                style={{
                  padding: "7px 8px", borderRadius: 8, marginBottom: 3, cursor: "pointer",
                  background: isSel ? "#7c3aed" : "#fff",
                  border: `1px solid ${isSel ? "#7c3aed" : "#e5e7eb"}`,
                  display: "flex", alignItems: "center", gap: 7,
                  boxShadow: isSel ? "0 2px 8px rgba(124,58,237,0.22)" : "0 1px 2px rgba(0,0,0,0.04)",
                  transition: "all 0.12s",
                }}
              >
                <div style={{
                  width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                  background: isSel ? "rgba(255,255,255,0.18)" : `${gc}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, color: isSel ? "#fff" : gc, fontWeight: 700,
                }}>
                  {meta?.icon}
                </div>
                <span style={{ flex: 1, fontSize: 11.5, fontWeight: isSel ? 600 : 400, color: isSel ? "#fff" : "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {meta?.label || comp.type}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => moveComp(idx, -1)} style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 3px", borderRadius: 3, color: isSel ? "rgba(255,255,255,0.7)" : "#9ca3af", fontSize: 10, lineHeight: 1 }}>↑</button>
                  <button onClick={() => moveComp(idx, 1)}  style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 3px", borderRadius: 3, color: isSel ? "rgba(255,255,255,0.7)" : "#9ca3af", fontSize: 10, lineHeight: 1 }}>↓</button>
                  <button onClick={() => deleteComp(comp.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 4px", borderRadius: 3, color: isSel ? "rgba(255,255,255,0.7)" : "#ef4444", fontSize: 13, lineHeight: 1, fontWeight: 700 }}>×</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER: Properties panel */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {selectedComp ? (
          <>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", background: "#fff", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                background: `${selectedGroupColor}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, color: selectedGroupColor,
              }}>
                {selectedMeta?.icon}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{selectedMeta?.label || selectedComp.type}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{selectedMeta?.group} component</div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
              <CompPropsEditor comp={selectedComp} screens={screens} onChange={updateComp} />
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#d1d5db", gap: 8 }}>
            <div style={{ fontSize: 40 }}>☜</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#9ca3af" }}>No component selected</div>
            <div style={{ fontSize: 12, color: "#c4c9d0" }}>Click a component in the list to configure it</div>
          </div>
        )}
      </div>

      {/* RIGHT: Component palette */}
      <div style={{ width: 178, borderLeft: "1px solid #e5e7eb", background: "#fafafa", overflowY: "auto", padding: "10px 10px" }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
          Add Component
        </div>
        {compGroups.map((grp) => {
          const gc = GROUP_COLOR[grp] || "#7c3aed";
          return (
            <div key={grp} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                <div style={{ width: 3, height: 10, background: gc, borderRadius: 2 }} />
                <span style={{ fontSize: 9, fontWeight: 700, color: gc, letterSpacing: 0.7 }}>{grp.toUpperCase()}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                {COMPONENT_TYPES.filter((t) => t.group === grp).map((t) => (
                  <div
                    key={t.type}
                    onClick={() => addComponent(t.type)}
                    title={t.label}
                    style={{
                      padding: "8px 4px 6px", borderRadius: 8, background: "#fff",
                      border: "1px solid #e5e7eb", cursor: "pointer", textAlign: "center",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                      transition: "border-color 0.12s, background 0.12s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = gc; e.currentTarget.style.background = `${gc}0a`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#fff"; }}
                  >
                    <span style={{ fontSize: 15 }}>{t.icon}</span>
                    <span style={{ fontSize: 9, color: "#6b7280", fontWeight: 500, lineHeight: 1.2, wordBreak: "break-word" }}>{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Field Mapping Tab ──────────────────────────────────────── */
const FieldMappingTab = ({ screens, fieldMappings, onChange }) => {
  const inputComps = screens.flatMap((s) =>
    (s.components || [])
      .filter((c) => c.props?.name)
      .map((c) => ({ screenId: s.id, screenTitle: s.title || s.id, compName: c.props.name, compLabel: c.props.label || c.type }))
  );

  const getMapping = (screenId, compName) =>
    fieldMappings.find((m) => m.screenId === screenId && m.componentName === compName)?.contactField || undefined;

  const setMapping = (screenId, compName, contactField) => {
    const without = fieldMappings.filter((m) => !(m.screenId === screenId && m.componentName === compName));
    if (contactField) {
      onChange([...without, { screenId, componentName: compName, contactField }]);
    } else {
      onChange(without);
    }
  };

  if (inputComps.length === 0) {
    return <div style={{ color: "#9ca3af", fontSize: 12 }}>Add input components (TextInput, DatePicker, etc.) to your screens first.</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        Map form fields to contact record fields. When user submits the form, mapped values are saved automatically.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            <th style={{ padding: "6px 10px", textAlign: "left", border: "1px solid #e5e7eb" }}>Screen</th>
            <th style={{ padding: "6px 10px", textAlign: "left", border: "1px solid #e5e7eb" }}>Form Field</th>
            <th style={{ padding: "6px 10px", textAlign: "left", border: "1px solid #e5e7eb" }}>Save to Contact Field</th>
          </tr>
        </thead>
        <tbody>
          {inputComps.map((comp, idx) => (
            <tr key={idx}>
              <td style={{ padding: "5px 10px", border: "1px solid #e5e7eb", color: "#6b7280" }}>{comp.screenTitle}</td>
              <td style={{ padding: "5px 10px", border: "1px solid #e5e7eb" }}>
                <code style={{ fontSize: 11, background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>{comp.compName}</code>
                {comp.compLabel && <span style={{ marginLeft: 6, color: "#9ca3af" }}>{comp.compLabel}</span>}
              </td>
              <td style={{ padding: "5px 10px", border: "1px solid #e5e7eb" }}>
                <Select
                  value={getMapping(comp.screenId, comp.compName)}
                  onChange={(v) => setMapping(comp.screenId, comp.compName, v)}
                  size="small"
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="— not saved —"
                  options={CONTACT_FIELDS}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─── Main Modal ─────────────────────────────────────────────── */
const FlowEditorModal = ({ open, flow, onClose, onSaved }) => {
  const isEdit = !!flow;

  const [name, setName]                 = useState("");
  const [description, setDescription]   = useState("");
  const [category, setCategory]         = useState("LEAD_GENERATION");
  const [isDynamic, setIsDynamic]       = useState(false);
  const [screens, setScreens]           = useState([]);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [activeScreenIdx, setActiveScreenIdx] = useState(0);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (!open) return;
    if (flow) {
      setName(flow.name || "");
      setDescription(flow.description || "");
      setCategory(flow.category || "LEAD_GENERATION");
      setIsDynamic(flow.isDynamic || false);
      setScreens(flow.screens || []);
      setFieldMappings(flow.fieldMappings || []);
      setActiveScreenIdx(0);
    } else {
      setName("Untitled Flow");
      setDescription("");
      setCategory("LEAD_GENERATION");
      setIsDynamic(false);
      // Default: one empty screen
      setScreens([{ id: "SCREEN_1", title: "Step 1", terminal: true, success: true, refreshOnBack: false, components: [] }]);
      setFieldMappings([]);
      setActiveScreenIdx(0);
    }
  }, [open, flow]);

  const addScreen = () => {
    const s = { id: screenUid(), title: `Step ${screens.length + 1}`, terminal: false, success: true, refreshOnBack: false, components: [] };
    const updated = [...screens, s];
    setScreens(updated);
    setActiveScreenIdx(updated.length - 1);
  };

  const deleteScreen = (idx) => {
    const updated = screens.filter((_, i) => i !== idx);
    setScreens(updated);
    setActiveScreenIdx(Math.min(activeScreenIdx, updated.length - 1));
  };

  const updateScreen = (idx, screen) => {
    const updated = [...screens];
    updated[idx] = screen;
    setScreens(updated);
  };

  const handleSave = async () => {
    if (!name.trim()) return message.error("Flow name is required.");
    setSaving(true);
    try {
      const payload = { name, description, category, isDynamic, screens, fieldMappings };
      if (isEdit) {
        const { data: res } = await api.put(`/api/meta-flows/${flow._id}`, payload);
        if (res.revertedToDraft) {
          message.warning("Flow saved and reverted to Draft — re-push and re-publish to go live again.");
        } else {
          message.success("Flow updated");
        }
      } else {
        await api.post("/api/meta-flows", payload);
        message.success("Flow created");
      }
      onSaved();
    } catch (err) {
      message.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Modal
      open={open}
      onCancel={onClose}
      title={isEdit ? `Edit Flow — ${flow?.name}` : "Create WhatsApp Flow"}
      width="94vw"
      style={{ top: 20 }}
      styles={{ body: { height: "80vh", overflowY: "hidden", padding: 0 } }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving} style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
            {isEdit ? "Save Changes" : "Create Flow"}
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", height: "100%" }}>
        {/* Sidebar — dark theme */}
        <div style={{
          width: 215, flexShrink: 0,
          background: "#1e293b",
          display: "flex", flexDirection: "column",
          borderRight: "1px solid #0f172a",
        }}>
          {/* Flow metadata */}
          <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Flow name</div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.12)", color: "#fff", marginBottom: 10 }}
            />
            <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Category</div>
            <Select
              value={category}
              onChange={setCategory}
              size="small"
              style={{ width: "100%", marginBottom: 10 }}
              options={CATEGORIES}
            />
            <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Description</div>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              rows={2}
              style={{ marginBottom: 10 }}
            />
            <div
              onClick={() => setIsDynamic(!isDynamic)}
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "5px 8px", borderRadius: 7, background: isDynamic ? "rgba(124,58,237,0.2)" : "rgba(255,255,255,0.05)", border: `1px solid ${isDynamic ? "rgba(124,58,237,0.4)" : "transparent"}` }}
            >
              <Switch size="small" checked={isDynamic} onChange={setIsDynamic} onClick={(_, e) => e.stopPropagation()} />
              <span style={{ fontSize: 11, color: isDynamic ? "#c4b5fd" : "rgba(255,255,255,0.55)" }}>Dynamic (server-driven)</span>
            </div>
          </div>

          {/* Screens list */}
          <div style={{ flex: 1, padding: "10px 12px 8px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
              Screens
            </div>
            {screens.map((s, idx) => {
              const isActive = activeScreenIdx === idx;
              return (
                <div
                  key={s.id}
                  onClick={() => setActiveScreenIdx(idx)}
                  style={{
                    padding: "8px 10px", borderRadius: 9, marginBottom: 4, cursor: "pointer",
                    background: isActive ? "rgba(124,58,237,0.28)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${isActive ? "rgba(124,58,237,0.5)" : "transparent"}`,
                    display: "flex", alignItems: "center", gap: 8,
                    transition: "all 0.12s",
                  }}
                >
                  <div style={{
                    width: 24, height: 24, borderRadius: 7, flexShrink: 0, fontSize: 10, fontWeight: 700,
                    background: isActive ? "#7c3aed" : "rgba(255,255,255,0.1)",
                    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {idx + 1}
                  </div>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? "#fff" : "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.title || s.id}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    {s.terminal && (
                      <span style={{ fontSize: 8, fontWeight: 700, background: "rgba(16,185,129,0.18)", color: "#34d399", padding: "1px 5px", borderRadius: 3 }}>END</span>
                    )}
                    {screens.length > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteScreen(idx); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.25)", fontSize: 15, padding: "0 1px", lineHeight: 1, fontWeight: 700 }}
                      >×</button>
                    )}
                  </div>
                </div>
              );
            })}
            <button
              onClick={addScreen}
              style={{
                marginTop: 4, padding: "8px 10px", borderRadius: 9,
                border: "1px dashed rgba(124,58,237,0.35)", background: "transparent",
                color: "#a78bfa", fontSize: 12, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                transition: "all 0.12s",
              }}
            >
              <PlusOutlined style={{ fontSize: 10 }} />
              Add Screen
            </button>
          </div>
        </div>

        {/* Main area: Tabs */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Tabs
            size="small"
            style={{ padding: "0 12px", flex: 1, overflow: "hidden" }}
            items={[
              {
                key: "screen",
                label: `Screen: ${screens[activeScreenIdx]?.title || screens[activeScreenIdx]?.id || ""}`,
                children: (
                  <div style={{ height: "calc(80vh - 110px)", overflow: "hidden" }}>
                    {screens[activeScreenIdx] && (
                      <ScreenEditor
                        screen={screens[activeScreenIdx]}
                        screens={screens}
                        onUpdate={(s) => updateScreen(activeScreenIdx, s)}
                        onDelete={() => deleteScreen(activeScreenIdx)}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: "mapping",
                label: "Field Mapping",
                children: (
                  <div style={{ padding: "4px 0", overflowY: "auto", height: "calc(80vh - 110px)" }}>
                    <FieldMappingTab
                      screens={screens}
                      fieldMappings={fieldMappings}
                      onChange={setFieldMappings}
                    />
                  </div>
                ),
              },
              {
                key: "preview",
                label: <span><EyeOutlined style={{ marginRight: 4 }} />Preview</span>,
                children: (
                  <div style={{ height: "calc(80vh - 110px)" }}>
                    <FlowPreview flow={{ name: name || "Flow", screens }} />
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </Modal>
    </>
  );
};

export default FlowEditorModal;
